import { useQuery } from '@apollo/client';
import * as R from 'ramda';
import { Controller, useFormContext } from 'react-hook-form';
import { graphql } from '~/apollo/generated/v4';
import { LithostratTargetType } from '~/apollo/generated/v4/graphql';
import { ReactSelectInput } from '~/components/ui/forms/react-select-input';
import { Select } from '~/components/ui/forms/select';
import type { LithostratLinkFormValues } from '~/utils/modules/lithostrat';
import { readableGqlEnum } from '~/utils/text';

const LITHOSTRAT_LINK_FORM_FIELDS_OPTIONS = graphql(`
  query LithostratLinkFormFieldsOptions {
    lithostratTypeList {
      results {
        id
        name
      }
    }
    groupList {
      results {
        id
        name
        lithostratTypeId
        wikiPageId
      }
    }
    formationList {
      results {
        id
        name
        lithostratTypeId
        wikiPageId
      }
    }
    memberList {
      results {
        id
        name
        lithostratTypeId
        wikiPageId
      }
    }
  }
`);

export function LithostratLinkFormFieldsV4() {
  const { watch, setValue, control } =
    useFormContext<LithostratLinkFormValues>();
  const { data } = useQuery(LITHOSTRAT_LINK_FORM_FIELDS_OPTIONS);

  const targetType = watch('targetType');
  const lithostratTypeIdValue = watch('lithostratTypeId');
  const lithostratTypeId = lithostratTypeIdValue
    ? parseInt(lithostratTypeIdValue)
    : undefined;

  const types =
    data?.lithostratTypeList?.results
      ?.slice()
      .sort(R.ascend(t => t.name ?? Infinity)) ?? [];

  const groups =
    data?.groupList?.results
      ?.slice()
      .sort(R.ascend(g => g.name ?? Infinity))
      .filter(
        g => !lithostratTypeId || g.lithostratTypeId === lithostratTypeId,
      ) ?? [];

  const formations =
    data?.formationList?.results
      ?.slice()
      .sort(R.ascend(f => f.name ?? Infinity))
      .filter(
        g => !lithostratTypeId || g.lithostratTypeId === lithostratTypeId,
      ) ?? [];

  const members =
    data?.memberList?.results
      ?.slice()
      .sort(R.ascend(m => m.name ?? Infinity))
      .filter(
        g => !lithostratTypeId || g.lithostratTypeId === lithostratTypeId,
      ) ?? [];

  const typeIdOptions =
    targetType === LithostratTargetType.Group
      ? groups
      : targetType === LithostratTargetType.Formation
        ? formations
        : targetType === LithostratTargetType.Member
          ? members
          : [];

  return (
    <div className="space-y-2">
      <Controller
        control={control}
        name="targetType"
        render={({ field }) => (
          <Select
            {...field}
            label="Type"
            options={Object.values(LithostratTargetType).map(opt => ({
              value: opt,
              label: readableGqlEnum(opt),
            }))}
            onChange={event => {
              setValue('targetType', String(event.target.value));
              setValue('targetId', '');
            }}
          />
        )}
      />

      <Controller
        control={control}
        name="lithostratTypeId"
        render={({ field }) => (
          <Select
            {...field}
            label="Lithostrat Type"
            options={types.map(t => ({
              value: t.id,
              label: t.name ?? `Lithostrat Type ${t.id}`,
            }))}
            onChange={event => {
              setValue('lithostratTypeId', String(event.target.value));
              setValue('targetId', '');
            }}
          />
        )}
      />

      <ReactSelectInput
        name="targetId"
        label="Target"
        options={typeIdOptions.map(t => ({
          value: t.id,
          label: t.name,
          disabled: !t.wikiPageId,
        }))}
        onChange={value => {
          setValue('targetId', String(value ?? ''));
        }}
        disabled={!typeIdOptions.length}
      />
    </div>
  );
}
